import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { LocalizationService } from '../../core/localization/localization.service';
import { TabTitleService } from '../../layout/tab-title.service';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinSearchAccordionComponent } from '../../shared/design-system/search-accordion.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { FinanceApiService, PlatformPartyRecord } from '../finance/finance-api.service';

const PARTY_KIND_ROUTE_MAP: Record<string, string> = {
  individual: 'Individual',
  organization: 'Organization',
  device: 'Device',
  'ai-agent': 'AiAgent',
  service: 'Service'
};

const PARTY_KIND_CREATE_QUERY: Record<string, number> = {
  individual: 0,
  organization: 1,
  device: 2,
  'ai-agent': 3,
  service: 4
};

@Component({
  standalone: true,
  selector: 'ain-platform-parties',
  imports: [AinPageComponent, AinSearchAccordionComponent, AinStateComponent, InfoHintComponent, FormsModule, TableModule, ButtonModule, SelectModule],
  template: `
    <ain-page [eyebrow]="t('platform.eyebrow')" [title]="pageTitle()" [description]="pageDescription()">
      <ain-info-hint
        page-title-addon
        [titleKey]="'partyHub.help.partiesList.title'"
        [descriptionKey]="'partyHub.help.partiesList.description'"
        [bulletKeys]="[
          'partyHub.help.partiesList.bullets.kindFilter',
          'partyHub.help.partiesList.bullets.createKind',
          'partyHub.help.partiesList.bullets.compliance'
        ]" />

      <div page-actions>
        <button pButton type="button" [label]="t('common.create')" (click)="openCreate()"></button>
      </div>

      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else {
        <ain-search-accordion [title]="t('platform.search.title')">
          <section class="search-grid">
            <input [(ngModel)]="filters.query" [placeholder]="t('platform.parties.searchPlaceholder')" />
            <p-select
              class="status-select"
              [(ngModel)]="filters.status"
              [options]="statusFilterOptions()"
              optionLabel="label"
              optionValue="value"
              [showClear]="true"
              [placeholder]="t('common.all')"
              appendTo="body" />
            <button pButton type="button" [label]="t('platform.search.apply')" (click)="load()"></button>
            <button pButton type="button" severity="secondary" [label]="t('platform.search.clear')" (click)="clearFilters()"></button>
          </section>
        </ain-search-accordion>

        @if (!rows().length) {
          <ain-state type="empty" [title]="t('common.noRecords')" />
        } @else {
          <p-table [value]="rows()">
            <ng-template pTemplate="header">
              <tr>
                <th>{{ t('platform.parties.fields.displayName') }}</th>
                <th>{{ t('platform.parties.fields.legalName') }}</th>
                <th>{{ t('platform.parties.fields.kind') }}</th>
                <th>{{ t('columns.status') }}</th>
                <th>{{ t('platform.parties.fields.actions') }}</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr>
                <td>{{ row.displayName }}</td>
                <td>{{ row.legalName || '-' }}</td>
                <td>{{ kindLabel(row.kind) }}</td>
                <td>{{ statusLabel(row.status) }}</td>
                <td class="actions">
                  <button pButton type="button" size="small" [label]="t('common.edit')" text="true" (click)="openDetail(row.id)"></button>
                  <button pButton type="button" size="small" [label]="t('platform.parties.actions.activate')" text="true" severity="success" [disabled]="isStatus(row, 0)" (click)="activate(row.id)"></button>
                  <button pButton type="button" size="small" [label]="t('platform.parties.actions.deactivate')" text="true" severity="warn" [disabled]="isStatus(row, 1)" (click)="deactivate(row.id)"></button>
                </td>
              </tr>
            </ng-template>
          </p-table>
        }
      }

      @if (errorMessage()) {
        <p class="message error">{{ errorMessage() }}</p>
      }
    </ain-page>
  `,
  styles: [`
    .search-grid { display: grid; grid-template-columns: 2fr 1fr auto auto; gap: 8px; align-items: center; }
    .search-grid input { height: 38px; border: 1px solid #cbd5e1; border-radius: 8px; padding: 0 10px; width: 100%; }
    .status-select { width: 100%; }
    .actions { display: flex; flex-wrap: wrap; gap: 4px; }
    .message.error { margin-top: 10px; color: #b91c1c; }
    @media (max-width: 1200px) { .search-grid { grid-template-columns: 1fr; } }
  `]
})
export class PlatformPartiesComponent implements OnInit {
  private readonly api = inject(FinanceApiService);
  private readonly localization = inject(LocalizationService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly tabTitleService = inject(TabTitleService);

  readonly loading = signal(false);
  readonly rows = signal<PlatformPartyRecord[]>([]);
  readonly errorMessage = signal('');
  readonly canLoad = computed(() => !this.loading());
  readonly kindFilter = signal<string | null>(null);
  readonly pageTitle = computed(() => {
    const kindKey = this.kindFilter();
    if (!kindKey) {
      return this.t('nav.partyHub.allParties');
    }

    const labelKey = this.kindNavLabelKey(kindKey);
    return labelKey ? this.t(labelKey) : this.t('nav.partyHub.allParties');
  });
  readonly pageDescription = computed(() => {
    const kindKey = this.kindFilter();
    if (!kindKey) {
      return this.t('platform.parties.description');
    }

    return this.t('platform.parties.kindFilteredDescription');
  });

  filters = {
    query: '',
    status: null as number | null
  };

  ngOnInit(): void {
    this.route.paramMap.subscribe(params => {
      const kindKey = params.get('kindKey');
      this.kindFilter.set(kindKey);
      this.syncTabTitle();
      this.load();
    });
  }

  load(): void {
    if (!this.canLoad()) {
      return;
    }

    this.loading.set(true);
    this.errorMessage.set('');
    const kind = this.resolveKindApiValue(this.kindFilter());
    this.api.listPlatformParties({
      query: this.filters.query,
      kind: kind ?? undefined,
      status: this.filters.status === null ? '' : String(this.filters.status),
      skip: 0,
      take: 200
    }).subscribe({
      next: response => {
        this.rows.set(response.items);
        this.loading.set(false);
      },
      error: () => {
        this.rows.set([]);
        this.errorMessage.set(this.t('common.loadFailed'));
        this.loading.set(false);
      }
    });
  }

  clearFilters(): void {
    this.filters.query = '';
    this.filters.status = null;
    this.load();
  }

  statusFilterOptions(): Array<{ value: number; label: string }> {
    return [
      { value: 0, label: this.statusLabel(0) },
      { value: 1, label: this.statusLabel(1) },
      { value: 2, label: this.statusLabel(2) }
    ];
  }

  openCreate(): void {
    const kindKey = this.kindFilter()?.toLowerCase();
    const kind = kindKey ? PARTY_KIND_CREATE_QUERY[kindKey] : undefined;
    void this.router.navigate(['/platform/parties/new'], {
      queryParams: kind !== undefined ? { kind } : undefined
    });
  }

  openDetail(partyId: string): void {
    void this.router.navigate(['/platform/parties', partyId]);
  }

  activate(partyId: string): void {
    this.api.activatePlatformParty(partyId).subscribe({
      next: () => this.load(),
      error: () => this.errorMessage.set(this.t('common.saveFailed'))
    });
  }

  deactivate(partyId: string): void {
    this.api.deactivatePlatformParty(partyId).subscribe({
      next: () => this.load(),
      error: () => this.errorMessage.set(this.t('common.saveFailed'))
    });
  }

  isStatus(party: PlatformPartyRecord, value: number): boolean {
    const current = typeof party.status === 'string' ? Number(party.status) : party.status;
    return current === value;
  }

  statusLabel(value: number | string): string {
    const numeric = typeof value === 'string' ? Number(value) : value;
    const map: Record<number, string> = {
      0: this.t('platform.parties.status.active'),
      1: this.t('platform.parties.status.inactive'),
      2: this.t('platform.parties.status.merged')
    };
    return map[numeric] ?? `${value}`;
  }

  kindLabel(value: number | string): string {
    const numeric = typeof value === 'string' ? Number(value) : value;
    const map: Record<number, string> = {
      0: this.t('platform.parties.kind.individual'),
      1: this.t('platform.parties.kind.organization'),
      2: this.t('platform.parties.kind.device'),
      3: this.t('platform.parties.kind.aiAgent'),
      4: this.t('platform.parties.kind.service')
    };
    return map[numeric] ?? `${value}`;
  }

  t(key: string): string {
    return this.localization.translate(key);
  }

  private resolveKindApiValue(kindKey: string | null): string | null {
    if (!kindKey) {
      return null;
    }

    return PARTY_KIND_ROUTE_MAP[kindKey.toLowerCase()] ?? null;
  }

  private kindNavLabelKey(kindKey: string): string | null {
    const map: Record<string, string> = {
      individual: 'nav.partyHub.individuals',
      organization: 'nav.partyHub.organizations',
      device: 'nav.partyHub.devices',
      'ai-agent': 'nav.partyHub.aiAgents',
      service: 'nav.partyHub.services'
    };
    return map[kindKey.toLowerCase()] ?? null;
  }

  private syncTabTitle(): void {
    this.tabTitleService.setCurrent(this.pageTitle());
  }
}
